import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useMutation } from "react-apollo-hooks";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import { feeValueArray, feeDayArray } from "../User/UserNames";

// 회원별 회비 납부 정보 수정 모달.
// 관리자(AdminFeeHasUserEdit)와 담당자(ManagerFeeHasUserEdit)가 같은 화면을 쓰고,
// 뮤테이션만 prop 으로 받아 갈아끼운다.
const FeeHasUserEditModal = ({
  feeHasUser,      // { id, feeDay, feeValue, feeDefault, feeAmount }
  memberName,
  mutation,        // gql 문서
  resultKey,       // 응답에서 꺼낼 키 이름
  onDone,          // 성공 시 호출 (refetch 함수 방식)
  refetchQueries,  // 또는 apollo refetchQueries 방식
}) => {
  const [open, setOpen] = useState(false);
  const [feeDay, setFeeDay] = useState(String(feeHasUser.feeDay || 5));
  const [feeValue, setFeeValue] = useState(String(feeHasUser.feeValue || 0));
  const [feeDefault, setFeeDefault] = useState(
    String(feeHasUser.feeDefault || 0)
  );
  const [saving, setSaving] = useState(false);

  const [editMutation] = useMutation(
    mutation,
    refetchQueries ? { refetchQueries } : undefined
  );

  // 목록이 갱신되면 현재 값으로 되돌린다(모달을 닫은 뒤 다시 열 때 최신값 표시).
  useEffect(() => {
    if (!open) {
      setFeeDay(String(feeHasUser.feeDay || 5));
      setFeeValue(String(feeHasUser.feeValue || 0));
      setFeeDefault(String(feeHasUser.feeDefault || 0));
    }
  }, [open, feeHasUser.feeDay, feeHasUser.feeValue, feeHasUser.feeDefault]);

  const toggle = () => setOpen(!open);

  const onlyDigits = (v) => v.replace(/[^0-9]/g, "");

  const changed =
    parseInt(feeDay || 0) !== (feeHasUser.feeDay || 0) ||
    parseInt(feeValue || 0) !== (feeHasUser.feeValue || 0) ||
    parseInt(feeDefault || 0) !== (feeHasUser.feeDefault || 0);

  const onSave = async () => {
    if (feeValue === "" || feeDefault === "") {
      toast.error("금액을 입력해주세요.");
      return;
    }
    if (!changed) {
      toast.error("변경된 값이 없습니다.");
      return;
    }

    const nextDefault = parseInt(feeDefault);
    if (nextDefault !== (feeHasUser.feeDefault || 0)) {
      if (
        !window.confirm(
          `미납금액을 ${(feeHasUser.feeDefault || 0).toLocaleString()}원에서 ` +
            `${nextDefault.toLocaleString()}원으로 변경합니다.\n\n` +
            `회비 전체 미납 합계도 같은 폭으로 조정됩니다. 진행하시겠습니까?`
        )
      ) {
        return;
      }
    }

    setSaving(true);
    try {
      const { data } = await editMutation({
        variables: {
          id: feeHasUser.id,
          feeDay: parseInt(feeDay),
          feeValue: parseInt(feeValue),
          feeDefault: nextDefault,
        },
      });
      if (data && data[resultKey]) {
        toast.success("수정되었습니다.");
        setOpen(false);
        if (onDone) onDone();
      } else {
        toast.error("수정에 실패했습니다.");
      }
    } catch (e) {
      toast.error(String(e).replace("Error: GraphQL error:", "").trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button className="btn btn-white btn-xs text-blue m-r-5" onClick={toggle}>
        수정
      </button>

      <Modal isOpen={open} toggle={toggle}>
        <ModalHeader toggle={toggle}>{memberName} 납부 정보 수정</ModalHeader>
        <ModalBody>
          <div className="form-group row m-b-15">
            <label className="col-form-label col-md-4">출금일</label>
            <div className="col-md-8">
              <select
                className="form-control"
                value={feeDay}
                onChange={(e) => setFeeDay(e.target.value)}
              >
                {feeDayArray.map((d) => (
                  <option key={d} value={d}>
                    {d}일
                  </option>
                ))}
              </select>
              <small className="text-muted">
                징수는 매월 5일 · 25일에만 실행됩니다.
              </small>
            </div>
          </div>

          <div className="form-group row m-b-15">
            <label className="col-form-label col-md-4">출금금액</label>
            <div className="col-md-8">
              <select
                className="form-control"
                value={feeValue}
                onChange={(e) => setFeeValue(e.target.value)}
              >
                {/* 기존 값이 선택지에 없으면(과거 등록분) 맨 위에 그대로 보여준다. */}
                {!feeValueArray.includes(parseInt(feeValue)) && (
                  <option value={feeValue}>
                    {parseInt(feeValue || 0).toLocaleString()} 드림 (기존 값)
                  </option>
                )}
                {feeValueArray.map((v) => (
                  <option key={v} value={v}>
                    {v.toLocaleString()} 드림
                  </option>
                ))}
              </select>
              <small className="text-muted">
                다음 출금일부터 적용됩니다. 과거 납부내역은 바뀌지 않습니다.
              </small>
            </div>
          </div>

          <div className="form-group row m-b-0">
            <label className="col-form-label col-md-4 text-red">미납금액</label>
            <div className="col-md-8">
              <input
                type="text"
                className="form-control text-right"
                value={feeDefault}
                onChange={(e) => setFeeDefault(onlyDigits(e.target.value))}
              />
              <small className="text-muted">
                면제 · 조정 시에만 변경하세요. 회비 전체 미납 합계도 함께
                조정됩니다.
              </small>
            </div>
          </div>

          <hr />
          <div className="f-s-12 text-muted">
            현재 납부총액{" "}
            <NumberFormat
              value={feeHasUser.feeAmount || 0}
              displayType={"text"}
              thousandSeparator={true}
            />
            원 — 수정해도 변하지 않습니다.
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-white" onClick={toggle} disabled={saving}>
            취소
          </button>
          <button
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving || !changed}
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FeeHasUserEditModal;
